import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function HistoryPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [mobileEquipmentOpen, setMobileEquipmentOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set([0, 1, 2, 3, 4, 5, 6]));
  const [animatedStats, setAnimatedStats] = useState([0, 0, 0, 0]);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  // 통계 카운트업 애니메이션
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const targets = [2012, 14, 2, 1000];
            const duration = 2000;
            const steps = 60;
            const increment = duration / steps;

            let currentStep = 0;
            const timer = setInterval(() => {
              currentStep++;
              const progress = currentStep / steps;
              setAnimatedStats(targets.map(target => Math.floor(target * progress)));
              
              if (currentStep >= steps) {
                setAnimatedStats(targets);
                clearInterval(timer);
              }
            }, increment);

            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // 타임라인 아이템 스크롤 애니메이션
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'));
            setVisibleItems(prev => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px 0px 0px' }
    );

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const navLinks = [
    { href: '/#about', label: '회사소개', type: 'dropdown', subMenu: [
      { href: '/#about', label: '회사소개' },
      { href: '/history', label: '연혁' },
      { href: '/organization', label: '조직도' },
      { href: null, label: '오시는 길', scrollTo: 'map' },
    ]},
    { href: '/#process', label: '생산공정', type: 'process' },
    { href: '#equipment', label: '보유장비', type: 'dropdown', subMenu: [
      { href: '/equipment/production', label: '생산장비' },
      { href: '/equipment/welding', label: '용접장비' },
      { href: '/equipment/capability', label: '가공능력' },
      { href: '/equipment/materials', label: '가공 가능자재' },
    ]},
    { href: '#products', label: '제조물품', type: 'dropdown', subMenu: [
      { href: '/gallery', label: '갤러리' },
    ]},
  ];

  const handleGoToMap = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('map');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const handleGoToProcess = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('process');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  const historyItems = [
    { year: '2012', title: '회사 설립', desc: 'NS-TECH 창업. 판금가공 전문 제조기업으로 출발하여 서비스 시작.', highlight: false },
    { year: '2014', title: 'BYSTRONIC 레이저 장비 도입', desc: '스위스 BYSTRONIC 고출력 CO2 레이저 커팅 장비 도입으로 가공 정밀도 및 생산성 대폭 향상.', highlight: false },
    { year: '2021', title: '신규 BYSTRONIC 파이버 레이저 장비 도입', desc: '스위스 BYSTRONIC 최신 파이버 레이저 장비 추가 도입. 고속·고정밀 가공 역량 강화 및 생산 효율 극대화.', highlight: false },
    { year: '2022', title: '조달청 입찰 제품 등록', desc: '조달청 나라장터 입찰 가능 제품 등록 완료. 공공기관 납품 채널 확대 및 직접생산 증명서 취득.', highlight: false },
    { year: '2022', title: '대기업 협력사 등록', desc: '국내 주요 대기업 협력사로 공식 등록. 안정적인 품질 관리 시스템 구축.', highlight: false },
    { year: '2023', title: '레이저 용접 라인 증설', desc: 'MIG·TIG·CO₂·레이저 용접 전 라인 구축 완료. 다양한 소재 및 두께에 대응 가능한 종합 용접 역량 확보.', highlight: false },
    { year: '2024', title: '원스톱 통합 생산 체계 완성', desc: 'CAD 설계 → 레이저 가공 → 절곡 → 용접 → 도장 → 조립 → 납품까지 전 공정 내재화. 누적 납품 실적 1,000건 돌파.', highlight: true },
  ];

  const stats = [
    { icon: 'ri-calendar-line', value: '2012', label: '설립연도', target: 2012 },
    { icon: 'ri-award-line', value: '14년+', label: '업력', target: 14 },
    { icon: 'ri-building-2-line', value: '2개', label: '생산 공장', target: 2 },
    { icon: 'ri-truck-line', value: '1,000+', label: '누적 납품 실적', target: 1000 },
  ];

  return (
    <>
      <SEOHead
        title="회사 연혁 | NS-TECH 판금가공"
        description="2012년 창업 이후 끊임없는 기술 혁신과 품질 향상으로 성장해온 NS-TECH의 발자취. 14년간의 판금가공 전문 기업 역사를 소개합니다."
        keywords="NS-TECH 연혁, 회사 역사, 판금가공 전문기업, 레이저 장비 도입, 기업 성장, 제조업체"
        ogType="website"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'NS-TECH 회사 연혁',
            description: '2012년 창업 이후 14년간의 기술 혁신과 성장 과정',
            url: `${import.meta.env.VITE_SITE_URL}/history`,
            mainEntity: {
              '@type': 'Organization',
              name: 'NS-TECH',
              foundingDate: '2012',
              url: import.meta.env.VITE_SITE_URL,
              address: {
                '@type': 'PostalAddress',
                addressLocality: '포천시',
                addressRegion: '경기도',
                streetAddress: '소흘읍 소흘로 11',
                addressCountry: 'KR',
              },
            },
          },
        ]}
      />
      <div className="min-h-screen bg-white">
        {/* 네비게이션 */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
          <div className="w-full px-4 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="hidden lg:flex items-center gap-2">
                <Link to="/" className="flex items-center gap-2 shrink-0 mr-2">
                  <img
                    src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                    alt="NS-TECH Logo"
                    className="h-12 w-auto object-contain"
                  />
                </Link>

                {navLinks.map((link) => (
                  link.type === 'dropdown' ? (
                    <div
                      key={link.label}
                      className="relative"
                      onMouseEnter={() => {
                        if (link.label === '보유장비') setEquipmentDropdownOpen(true);
                        if (link.label === '제조물품') setProductsDropdownOpen(true);
                        if (link.label === '회사소개') setAboutDropdownOpen(true);
                      }}
                      onMouseLeave={() => {
                        if (link.label === '보유장비') setEquipmentDropdownOpen(false);
                        if (link.label === '제조물품') setProductsDropdownOpen(false);
                        if (link.label === '회사소개') setAboutDropdownOpen(false);
                      }}
                    >
                      <button className={`text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md flex items-center gap-1 cursor-pointer ${
                        link.label === '회사소개' ? 'text-orange-500 bg-orange-50' : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                      }`}>
                        {link.label}
                        <i className={`ri-arrow-down-s-line transition-transform ${
                          (link.label === '보유장비' && equipmentDropdownOpen) ||
                          (link.label === '제조물품' && productsDropdownOpen) ||
                          (link.label === '회사소개' && aboutDropdownOpen)
                            ? 'rotate-180' : ''
                        }`}></i>
                      </button>
                      {((link.label === '보유장비' && equipmentDropdownOpen) ||
                        (link.label === '제조물품' && productsDropdownOpen) ||
                        (link.label === '회사소개' && aboutDropdownOpen)) && (
                        <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-lg py-2 min-w-[160px] border border-gray-100">
                          {link.subMenu?.map((subLink) => (
                            subLink.scrollTo === 'map' ? (
                              <button
                                key={subLink.label}
                                onClick={handleGoToMap}
                                className="block w-full text-left px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                              >
                                {subLink.label}
                              </button>
                            ) : (
                              <Link
                                key={subLink.href}
                                to={subLink.href!}
                                className={`block px-4 py-2 text-sm hover:text-orange-500 hover:bg-orange-50 transition-colors whitespace-nowrap ${
                                  subLink.href === '/history' ? 'text-orange-500 font-semibold' : 'text-gray-700'
                                }`}
                              >
                                {subLink.label}
                              </Link>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  ) : link.type === 'process' ? (
                    <button
                      key={link.label}
                      onClick={handleGoToProcess}
                      className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50 cursor-pointer"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      key={link.href}
                      to={link.href!}
                      className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                    >
                      {link.label}
                    </Link>
                  )
                ))}
              </div>

              <div className="flex lg:hidden items-center justify-between w-full">
                <Link to="/" className="flex items-center gap-2 shrink-0">
                  <img
                    src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
                    alt="NS-TECH Logo"
                    className="h-10 w-auto object-contain"
                  />
                </Link>
                <button
                  className="flex flex-col justify-center items-center w-10 h-10 gap-1.5 cursor-pointer relative z-[60]"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="메뉴 열기"
                >
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`block w-6 h-0.5 transition-all duration-300 bg-gray-800 ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                </button>
              </div>

              {/* 우측: 고객지원 버튼 (데스크탑만) */}
              <div className="hidden lg:flex items-center">
                <button
                  onClick={() => setShowContactModal(true)}
                  className="text-sm font-medium transition-colors whitespace-nowrap px-4 py-2 rounded-md text-gray-700 hover:text-orange-500 hover:bg-orange-50 cursor-pointer"
                >
                  고객지원
                </button>
              </div>
            </div>
          </div>

          <div
            className={`lg:hidden fixed inset-0 bg-black/50 transition-opacity duration-300 z-40 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            onClick={() => setMobileMenuOpen(false)}
          ></div>

          <div className={`lg:hidden fixed top-[60px] left-0 right-0 transition-all duration-300 ease-in-out z-50 ${mobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0 pointer-events-none'}`}>
            <div className="bg-white shadow-xl border-t border-gray-100 px-5 py-3 flex flex-col gap-1">
              {navLinks.map((link) => (
                link.type === 'dropdown' ? (
                  <div key={link.label}>
                    <button
                      onClick={() => {
                        if (link.label === '보유장비') setMobileEquipmentOpen(!mobileEquipmentOpen);
                        if (link.label === '제조물품') setMobileProductsOpen(!mobileProductsOpen);
                        if (link.label === '회사소개') setMobileAboutOpen(!mobileAboutOpen);
                      }}
                      className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50 flex items-center justify-between"
                    >
                      {link.label}
                      <i className={`ri-arrow-down-s-line transition-transform ${
                        (link.label === '보유장비' && mobileEquipmentOpen) ||
                        (link.label === '제조물품' && mobileProductsOpen) ||
                        (link.label === '회사소개' && mobileAboutOpen)
                          ? 'rotate-180' : ''
                      }`}></i>
                    </button>
                    {((link.label === '보유장비' && mobileEquipmentOpen) ||
                      (link.label === '제조물품' && mobileProductsOpen) ||
                      (link.label === '회사소개' && mobileAboutOpen)) && (
                      <div className="pl-4 mt-1 space-y-1">
                        {link.subMenu?.map((subLink) => (
                          subLink.scrollTo === 'map' ? (
                            <button
                              key={subLink.label}
                              onClick={() => { setMobileMenuOpen(false); handleGoToMap(); }}
                              className="block w-full text-left text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                            >
                              {subLink.label}
                            </button>
                          ) : (
                            <Link
                              key={subLink.href}
                              to={subLink.href!}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block text-sm font-medium py-2 px-4 rounded-lg transition-all cursor-pointer text-gray-600 hover:text-orange-500 hover:bg-orange-50"
                            >
                              {subLink.label}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : link.type === 'process' ? (
                  <button
                    key={link.label}
                    onClick={() => { setMobileMenuOpen(false); handleGoToProcess(); }}
                    className="w-full text-left text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                  >
                    {link.label}
                  </button>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href!}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-gray-700 hover:text-orange-500 hover:bg-orange-50"
                  >
                    {link.label}
                  </Link>
                )
              ))}
              {/* 모바일 고객지원 버튼 */}
              <button
                onClick={() => { setMobileMenuOpen(false); setShowContactModal(true); }}
                className="w-full text-sm font-medium py-2.5 px-4 rounded-lg transition-all cursor-pointer text-left text-gray-700 hover:text-orange-500 hover:bg-orange-50"
              >
                고객지원
              </button>
            </div>
          </div>
        </nav>

        {/* 헤더 */}
        <div className="pt-20 pb-0">
          <div className="relative h-56 md:h-80 overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/3e144c42bc4718796c44edce53f8d671.jpg"
              alt="NS-TECH 연혁"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
              <span className="text-orange-400 text-xs font-semibold uppercase tracking-widest mb-2 animate-[fadeIn_0.6s_ease-out]">Company History</span>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 animate-[fadeIn_0.8s_ease-out]">NS-TECH 연혁</h1>
              <p className="text-white/80 text-sm md:text-base max-w-xl animate-[fadeIn_1s_ease-out]">
                2012년 창업 이후 끊임없는 기술 혁신과 품질 향상으로 성장해온 NS-TECH의 발자취입니다.
              </p>
            </div>
          </div>
        </div>

        {/* 통계 */}
        <div ref={statsRef} className="bg-orange-500 py-8 md:py-10">
          <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="flex flex-col items-center text-center transform transition-all duration-500 hover:scale-110">
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full mb-3 animate-[pulse_2s_ease-in-out_infinite]">
                  <i className={`${stat.icon} text-white text-xl`}></i>
                </div>
                <p className="text-white text-2xl md:text-3xl font-bold">
                  {i === 0 ? animatedStats[i] : i === 1 ? `${animatedStats[i]}년+` : i === 2 ? `${animatedStats[i]}개` : `${animatedStats[i]}+`}
                </p>
                <p className="text-orange-100 text-xs md:text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 연혁 타임라인 */}
        <div className="py-16 md:py-28 px-5 md:px-20 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12 md:mb-20">
              <span className="text-orange-500 text-xs font-semibold uppercase tracking-widest">History</span>
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mt-2 mb-3">회사 연혁</h2>
              <div className="w-10 h-1 bg-orange-500 mx-auto rounded-full"></div>
              <p className="text-gray-500 text-sm mt-4 max-w-xl mx-auto">
                기술력과 신뢰를 바탕으로 성장해온 NS-TECH의 주요 이정표를 소개합니다.
              </p>
            </div>

            {/* 데스크탑 타임라인 */}
            <div className="hidden md:block relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-200 via-orange-300 to-orange-200 -translate-x-1/2"></div>
              <div className="space-y-0">
                {historyItems.map((item, idx) => {
                  const isLeft = idx % 2 === 0;
                  const isVisible = visibleItems.has(idx);
                  return (
                    <div 
                      key={idx} 
                      ref={el => itemRefs.current[idx] = el}
                      data-index={idx}
                      className="relative flex items-center mb-10"
                    >
                      <div className="w-1/2 pr-12 flex justify-end">
                        {isLeft && (
                          <div
                            className={`rounded-2xl p-6 shadow-sm border max-w-sm w-full hover:shadow-xl hover:scale-105 ${
                              item.highlight
                                ? 'bg-orange-500 border-orange-500 shadow-orange-200'
                                : 'bg-white border-gray-100'
                            }`}
                            style={{
                              transition: 'opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s',
                              opacity: isVisible ? 1 : 0,
                              transform: isVisible ? 'translateX(0)' : 'translateX(80px)',
                              transitionDelay: `${idx * 0.08}s`,
                            }}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                                item.highlight ? 'bg-white/20' : 'bg-orange-100'
                              }`}>
                                <span className={`text-2xl font-black ${item.highlight ? 'text-white' : 'text-orange-500'}`}>{item.year}</span>
                              </div>
                            </div>
                            <p className={`font-bold text-base mb-2 ${item.highlight ? 'text-white' : 'text-gray-900'}`}>{item.title}</p>
                            <p className={`text-sm leading-relaxed ${item.highlight ? 'text-orange-100' : 'text-gray-500'}`}>{item.desc}</p>
                            {item.highlight && (
                              <div className="mt-3 flex items-center gap-1.5 animate-[pulse_2s_ease-in-out_infinite]">
                                <i className="ri-star-fill text-yellow-300 text-sm"></i>
                                <span className="text-white text-xs font-semibold">주요 마일스톤</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="relative z-10 flex-shrink-0">
                        <div
                          className={`w-5 h-5 rounded-full border-4 border-white shadow-md ${
                            item.highlight ? 'bg-orange-500 animate-[pulse_2s_ease-in-out_infinite]' : 'bg-orange-400'
                          }`}
                          style={{
                            transition: 'transform 0.5s cubic-bezier(0.22,1,0.36,1)',
                            transitionDelay: `${idx * 0.08 + 0.2}s`,
                            transform: isVisible ? 'scale(1)' : 'scale(0)',
                          }}
                        ></div>
                      </div>
                      <div className="w-1/2 pl-12 flex justify-start">
                        {!isLeft && (
                          <div
                            className={`rounded-2xl p-6 shadow-sm border max-w-sm w-full hover:shadow-xl hover:scale-105 ${
                              item.highlight
                                ? 'bg-orange-500 border-orange-500 shadow-orange-200'
                                : 'bg-white border-gray-100'
                            }`}
                            style={{
                              transition: 'opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s',
                              opacity: isVisible ? 1 : 0,
                              transform: isVisible ? 'translateX(0)' : 'translateX(-80px)',
                              transitionDelay: `${idx * 0.08}s`,
                            }}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                                item.highlight ? 'bg-white/20' : 'bg-orange-100'
                              }`}>
                                <span className={`text-2xl font-black ${item.highlight ? 'text-white' : 'text-orange-500'}`}>{item.year}</span>
                              </div>
                            </div>
                            <p className={`font-bold text-base mb-2 ${item.highlight ? 'text-white' : 'text-gray-900'}`}>{item.title}</p>
                            <p className={`text-sm leading-relaxed ${item.highlight ? 'text-orange-100' : 'text-gray-500'}`}>{item.desc}</p>
                            {item.highlight && (
                              <div className="mt-3 flex items-center gap-1.5 animate-[pulse_2s_ease-in-out_infinite]">
                                <i className="ri-star-fill text-yellow-300 text-sm"></i>
                                <span className="text-white text-xs font-semibold">주요 마일스톤</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 모바일 타임라인 */}
            <div className="md:hidden relative">
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-200 via-orange-300 to-orange-200"></div>
              <div className="space-y-5">
                {historyItems.map((item, idx) => {
                  const isVisible = visibleItems.has(idx);
                  return (
                    <div
                      key={idx}
                      ref={el => itemRefs.current[idx] = el}
                      data-index={idx}
                      className="relative flex items-start gap-4 pl-12"
                      style={{
                        transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? 'translateX(0)' : 'translateX(-60px)',
                        transitionDelay: `${idx * 0.07}s`,
                      }}
                    >
                      <div
                        className={`absolute left-3.5 top-2 w-3.5 h-3.5 rounded-full border-2 border-white shadow -translate-x-1/2 ${
                          item.highlight ? 'bg-orange-500 animate-[pulse_2s_ease-in-out_infinite]' : 'bg-orange-500'
                        }`}
                        style={{
                          transition: 'transform 0.5s cubic-bezier(0.22,1,0.36,1)',
                          transitionDelay: `${idx * 0.07 + 0.15}s`,
                          transform: isVisible ? 'scale(1) translateX(-50%)' : 'scale(0) translateX(-50%)',
                        }}
                      ></div>
                      <div className={`flex-1 rounded-xl p-4 shadow-sm border transition-all hover:shadow-lg hover:scale-105 ${
                        item.highlight ? 'bg-orange-500 border-orange-500' : 'bg-white border-gray-100'
                      }`}>
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 ${
                          item.highlight ? 'bg-white/20' : 'bg-orange-100'
                        }`}>
                          <span className={`text-lg font-black ${item.highlight ? 'text-white' : 'text-orange-500'}`}>{item.year}</span>
                        </div>
                        <p className={`font-bold text-sm mt-1 ${item.highlight ? 'text-white' : 'text-gray-900'}`}>{item.title}</p>
                        <p className={`text-xs mt-1.5 leading-relaxed ${item.highlight ? 'text-orange-100' : 'text-gray-500'}`}>{item.desc}</p>
                        {item.highlight && (
                          <div className="mt-2 flex items-center gap-1 animate-[pulse_2s_ease-in-out_infinite]">
                            <i className="ri-star-fill text-yellow-300 text-xs"></i>
                            <span className="text-white text-xs font-semibold">주요 마일스톤</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* 하단 CTA */}
        <div className="py-12 md:py-20 px-5 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <img
              src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
              alt="NS-TECH Logo"
              className="h-16 w-auto mx-auto mb-6 object-contain"
            />
            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-3">
              NS-TECH와 함께 성장하세요
            </h2>
            <p className="text-gray-500 text-sm md:text-base mb-8">
              14년의 경험과 기술력으로 최적의 판금 가공 솔루션을 제공합니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/#quote"
                className="px-8 py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors shadow-md whitespace-nowrap cursor-pointer text-sm"
              >
                견적 문의하기
              </Link>
              <Link
                to="/"
                className="px-8 py-3 bg-white text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors border-2 border-gray-200 whitespace-nowrap cursor-pointer text-sm"
              >
                홈으로 돌아가기
              </Link>
            </div>
          </div>
        </div>

        {/* 푸터 */}
        <footer className="bg-orange-500 text-white py-8 px-5">
          <div className="max-w-5xl mx-auto text-center">
            <img
              src="https://static.readdy.ai/image/7455c6c7e49181bdad2ed4b44e4d279c/b09fe7069d8db297eae61b8e105adfe6.png"
              alt="NS-TECH Logo"
              className="h-10 w-auto mx-auto mb-4 brightness-0 invert"
            />
            <p className="text-white/80 text-xs mb-1">© 2025 NS-TECH. All rights reserved.</p>
            <p className="text-white/60 text-xs">경기도 포천시 소흘읍 소흘로 11 | Tel: 031-541-1902 | Email: nstech01@naver.com</p>
          </div>
        </footer>

        {/* 모바일 플로팅 전화 버튼 */}
        <a
          href="tel:031-541-1902"
          className="lg:hidden fixed bottom-24 right-4 z-50 flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white pl-3 pr-4 py-2.5 rounded-full shadow-xl transition-all active:scale-95 cursor-pointer"
          aria-label="전화 연결"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-phone-fill text-white text-base"></i>
          </div>
          <span className="text-xs font-bold whitespace-nowrap">전화문의</span>
        </a>

        {/* 연락처 모달 */}
        {showContactModal && (
          <div 
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={() => setShowContactModal(false)}
          >
            <div 
              className="bg-white rounded-3xl max-w-md w-full p-6 md:p-8 relative shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowContactModal(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                aria-label="닫기"
              >
                <i className="ri-close-line text-gray-600 text-xl"></i>
              </button>

              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-orange-500 text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">견적 문의</h3>
                <p className="text-sm text-gray-500">편하신 방법으로 연락주세요</p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-building-line text-orange-500"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">회사명</p>
                    <p className="text-base font-semibold text-gray-900">NS-TECH</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">전화번호</p>
                    <p className="text-base font-semibold text-gray-900">031-541-1902</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-printer-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">팩스</p>
                    <p className="text-base font-semibold text-gray-900">031-541-1903</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">이메일</p>
                    <p className="text-base font-semibold text-gray-900">nstech01@naver.com</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                    <i className="ri-time-line text-orange-500"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">운영시간</p>
                    <p className="text-base font-semibold text-gray-900">AM 08:00 ~ PM 18:00</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <a
                  href="tel:031-541-1902"
                  className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line"></i>
                  <span>전화하기</span>
                </a>
                <a
                  href="mailto:nstech01@naver.com"
                  className="flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line"></i>
                  <span>메일보내기</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
